import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { authLogout, selectGovBr } from '../../auth/authSlice';

import Accordion, { SCROLL_OFFSET_NAV_FIXED } from '../../components/Accordion/Accordion';
import Card from '../../components/Card/Card';
import MainContainer from '../../components/MainContainer/MainContainer';
import { selectLoading, selectLoadingCount } from '../../globalSlice';

import { selectLogado } from '../IdentificacaoPositiva/identificacaoPositivaSlice';
import styles from './ConsultaRenda.module.css';
import BrButton from '@govbr/react-components/lib/BrButton';
import 'react-loading-skeleton/dist/skeleton.css';
import * as Mensagens from '../../common/Mensagens';
import {
  alterarFamiliaSemRegistroCivil,
  alterarMembroSelecionado,
  alterarNumeroFamiliar,
  alterarRendaIdentificada,
  alterarSistemaParceiroIndisponivel,
  getDadosTrabalhoRendaPessoa,
  getDadosRendaCNIS,
  getIntegrantes,
  selectFamiliaSemRegistroCivil,
  selectIntegrantes,
  selectDadosRendaCNIS,
  getDadosRendaAtualizada,
  selectDadosRendaAtualizada,
  selectSistemaParceiroIndisponivel,
} from './consultaRendaSlice';
import { getSituacaoCadastroFamilia, selectPerfilCompleto, selectSituacaoCadastroFamilia } from '../Perfil/perfilSlice';
import { TipoPerfil } from '../../model/TipoPerfil';
import { trackServiceSelect } from '../../common/tracker';
import imgErro from '../../assets/img/icone-erro.png';
import Message from '../../components/Message/Message';
import AjudaRapida from '../../components/AjudaRapida/AjudaRapida';
import auth from '../../auth/Auth';
import { limpar as limparConsultaRenda}  from '../../features/ConsultaRenda/consultaRendaSlice';
import { obterNomeDoPerfil } from '../../common/perfilUtils';
import { obterNomeOuNomeSocial } from '../../common/integranteUtils';

const ConsultaRenda: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const perfil = useAppSelector(selectPerfilCompleto);

  const situacaoCadastroFamilia = useAppSelector(selectSituacaoCadastroFamilia);

  const govBrLogado = useAppSelector(selectGovBr).logado;
  const isAuthenticatedIdPositiva = useAppSelector(selectLogado);

  const integrantesFamiliares: any[] = useAppSelector(selectIntegrantes);

  let memoizedIntegrantes = useMemo(()=> integrantesFamiliares, [integrantesFamiliares]);

  const loadingCount = useAppSelector(selectLoadingCount);
  const isLoading = useAppSelector(selectLoading);

  const familiaSemRegistroCivil: any = useAppSelector(selectFamiliaSemRegistroCivil);

  const dadosRendaCNIS = useAppSelector(selectDadosRendaCNIS);
  const dadosRendaAtualizada = useAppSelector(selectDadosRendaAtualizada);

  const sistemaParceiroIndisponivel = useAppSelector(selectSistemaParceiroIndisponivel);

  const [accordionsRenderizados, setAccordionsRenderizados] = useState({});

  const toggleAccordionsRenderizados = (key) => {
    if (!accordionsRenderizados[key]) {
     if (!isLoading && loadingCount === 0) {
       setAccordionsRenderizados((prevState) => ({
         ...prevState,
         [key]: !prevState[key],
       }));
     }
    }
  };

  useEffect(() => {
    if (perfil && perfil.numeroFamiliar) {
      dispatch(alterarNumeroFamiliar(perfil.numeroFamiliar));
    }

    if (!!integrantesFamiliares && perfil && perfil.numeroFamiliar) {
      dispatch(getIntegrantes(perfil.numeroFamiliar));
    }
  }, [dispatch, situacaoCadastroFamilia, history]);

  useEffect(() => {
    if (!situacaoCadastroFamilia) {
      dispatch(getSituacaoCadastroFamilia({ cpf: auth.getUser(), history }));
    }
  },[situacaoCadastroFamilia]);

  useEffect(() => {
    obterDadosRendaMembroLogado();
  },[]);

  const obterDadosRendaMembroLogado = () => {
    if(perfil?.tipoPerfil?.codigo !== TipoPerfil.REPRESENTANTE_LEGAL) {
      let dados = {
        idPessoa: perfil?.identificador,
        nuFamiliar: perfil?.numeroFamiliar,
        cpf: perfil?.cpf
      }

      dispatch(getDadosRendaCNIS(dados));
      dispatch(getDadosRendaAtualizada(dados));
    }
  }

  const caixaMensagens = () => {
    trackServiceSelect('POR-MENU', "CAIXA DE MENSAGEM");
    history.push('/caixa-mensagem');
  };

  const isLogado = () => {
    return (govBrLogado && situacaoCadastroFamilia) || isAuthenticatedIdPositiva
      ? true
      : false;
  }

  const logout = () => {
    dispatch(limparConsultaRenda());
    dispatch(authLogout({ history }));
  };

  const mostrarParentesco = (membro: any) => {
    if (membro?.parentesco === 'Pessoa Responsável pela Unidade Familiar - RF') {
      return 'Responsável Familiar - RF';
    }
    return membro?.parentesco;
  };

  const consultarRendas = (membro: any, index: any) => {
    toggleAccordionsRenderizados(index);

    if (!membro.semRegistroCivil) {
      dispatch(alterarSistemaParceiroIndisponivel(false));
      dispatch(alterarMembroSelecionado(membro));

      let dados = {
        idPessoa: membro?.idPessoa,
        nuFamiliar: perfil?.numeroFamiliar,
        cpf: perfil?.cpf
      }

      dispatch(getDadosRendaCNIS(dados));
      dispatch(getDadosRendaAtualizada(dados));
    }
  }

  const exibirRendaIdentificada = (membro) => {
    dispatch(alterarMembroSelecionado(membro));
    dispatch(alterarRendaIdentificada(true));
    history.push("/renda");
    trackServiceSelect('RENDA IDENTIFICADA NO CNIS', "CONSULTA RENDA");
  }

  const exibirRendaAtualizadaPeloGovernoFederal = (membro: any) => {
    dispatch(alterarMembroSelecionado(membro));
    dispatch(alterarRendaIdentificada(false));
    history.push("/renda");
    trackServiceSelect('RENDA ATUALIZADA PELO GOVERNO FEDERAL ', "CONSULTA RENDA");
  }

  const exibirRendaCadastroUnico = (membro: any) => {
    dispatch(alterarMembroSelecionado(membro));
    dispatch(getDadosTrabalhoRendaPessoa(membro?.idPessoa));
    history.push("/rendaCadastroUnico");
    trackServiceSelect('RENDA NO CADASTRO ÚNICO', "CONSULTA RENDA");
  }

  const sair = () => {
    dispatch(alterarFamiliaSemRegistroCivil(false));
    dispatch(alterarSistemaParceiroIndisponivel(false));
    dispatch(limparConsultaRenda());
    history.goBack();
  }

  const temDadoRendaCNISParaMembro = (membro) => {
    if (dadosRendaCNIS && dadosRendaCNIS.length > 0) {
      return dadosRendaCNIS?.find(item => item.idPessoa == membro.idPessoa && item.dadosRenda.possuiAlgumaRemuneracao );
    }
    return false;
  }

  const temDadoRendaAtualizadaParaMembro = (membro) => {
    if (dadosRendaAtualizada && dadosRendaAtualizada.length > 0) {
      return dadosRendaAtualizada?.find(item => item.idPessoa == membro.idPessoa && item.dadosRenda.possuiAlgumaRemuneracao);
    }
    return false;
  }

  const consultasRendasDosMembrosFinalizadas = (membro) => {
    if (dadosRendaCNIS?.find(item => item.idPessoa == membro.idPessoa) && dadosRendaAtualizada?.find(item => item.idPessoa == membro.idPessoa)) {
      return true;
    } else {
      return false;
    }
  }

  const ehMembroLogado = (membro, perfil) => {
    return perfil?.tipoPerfil?.codigo !== TipoPerfil.REPRESENTANTE_LEGAL && perfil?.identificador === membro.idPessoa;
  }

  const telaPrincipal = (
    <>
      {!isLoading && loadingCount === 0 && familiaSemRegistroCivil &&
        <Card className={styles.card}>
          <div className={styles.containerMensagem}>
            <img className={styles.imgMessage} src={imgErro} alt="Imagem de erro"/>
            <Message type={'error'}
              text={
                <>
                  <div className={styles.message}>
                    <span>Família sem registro civil. Consulta de renda não disponível.
                      Responsável Familiar deve procurar o posto de atendimento do Cadastro Único para apresentar os documentos de identificação.</span>
                  </div>
                </>
              }
            />
            <div className={styles.botaoContainer}>
              <BrButton onClick={()=>sair()}
                className={styles.botao}>
                      OK
              </BrButton>
            </div>
          </div>
        </Card>
      }

      {!sistemaParceiroIndisponivel && !familiaSemRegistroCivil && (perfil?.tipoPerfil?.codigo === TipoPerfil.RESPONSAVEL_FAMILIAR ||
          perfil?.tipoPerfil?.codigo === TipoPerfil.REPRESENTANTE_LEGAL) &&
        <div className={styles.cardHeader}>
          <i className={`fas fa-address-book ${styles.cardIcon}`}></i>
          <span className={styles.cardHeaderTitle}>{memoizedIntegrantes?.length} integrante(s) da família</span>
        </div>
      }

      {!isLoading && loadingCount === 0 && sistemaParceiroIndisponivel &&
        <Card className={styles.card}>
          <div className={styles.containerMensagem}>
            <img className={styles.imgMessage} src={imgErro} alt="Imagem de erro"/>
            <Message type={'error'}
              text={
                <>
                  <div className={styles.message}>
                    <span>{Mensagens.MSG_TENTE_NOVAMENTE}</span>
                  </div>
                </>
              }
            />
            <div className={styles.botaoContainer}>
              <BrButton onClick={()=>dispatch(alterarSistemaParceiroIndisponivel(false))}
                className={styles.botao}>
                  OK
              </BrButton>
            </div>
          </div>
        </Card>
      }

      {!sistemaParceiroIndisponivel && memoizedIntegrantes.map((membro, index) => {
        if (perfil?.tipoPerfil?.codigo === TipoPerfil.RESPONSAVEL_FAMILIAR ||
          perfil?.tipoPerfil?.codigo === TipoPerfil.REPRESENTANTE_LEGAL ||
          perfil?.identificador === membro.idPessoa) {

            return (
              <div key={index}>
                <Accordion
                  id={`membro-${index}`}
                  title={obterNomeOuNomeSocial(membro)}
                  subtitle={mostrarParentesco(membro)}
                  key={index}
                  styleContent={styles.styleContent}
                  exibirSubtitulo
                  open={ehMembroLogado(membro, perfil)}
                  textoDetalhamento='Detalhar'
                  callback={() => consultarRendas(membro, index)}
                >
                  {!sistemaParceiroIndisponivel &&
                    <>
                      {membro.semRegistroCivil &&
                        <div className={styles.mensagemRegistroCivil} >Pessoa sem registro civil. Consulta de renda não disponível. Responsável Familiar deve procurar
                        o posto de atendimento do Cadastro Único para apresentar os documentos de identificação.</div>}

                      {(!membro.semRegistroCivil && (perfil?.tipoPerfil?.codigo === TipoPerfil.RESPONSAVEL_FAMILIAR ||
                          perfil?.tipoPerfil?.codigo === TipoPerfil.REPRESENTANTE_LEGAL ||
                          perfil?.identificador === membro.idPessoa)) &&
                        consultasRendasDosMembrosFinalizadas(membro) &&
                          <>
                            {temDadoRendaAtualizadaParaMembro(membro) &&
                              <Card className={`${styles.card} `}>
                                <div className={`${styles.blocoItemInvertido}`} onClick={() => exibirRendaAtualizadaPeloGovernoFederal(membro)}>
                                  <div className={styles.blocoItemInvertidoLeft}>
                                    <span className={styles.itemText}>Renda atualizada automaticamente pelo Governo Federal</span><br/>
                                  </div>
                                </div>
                              </Card>}

                            {temDadoRendaCNISParaMembro(membro) &&
                              <Card className={styles.card}>
                                <div className={`${styles.blocoItem}`} onClick={() => exibirRendaIdentificada(membro)}>
                                  <div className={styles.blocoItemLeft}>
                                    <span className={styles.itemText}>Renda identificada no CNIS</span><br/>
                                  </div>
                                </div>
                              </Card>}

                            {!temDadoRendaCNISParaMembro(membro) &&
                              <div className={styles.espacoMensagem}>
                                <Message type={'warning'}
                                  text={
                                    <>
                                      <div className={styles.message}>
                                        <span>Não encontramos informações sobre sua renda nas bases do Governo Federal.</span>
                                      </div>
                                    </>
                                  }
                                />
                              </div>
                            }
                          </>
                        }
                    </>
                  }
                </Accordion>
              </div>
            );
          }
      })}

      {!familiaSemRegistroCivil && !sistemaParceiroIndisponivel &&
        <AjudaRapida scrollOffset={SCROLL_OFFSET_NAV_FIXED}>
          <div className={styles.textoAjudaRapida}>
            <strong>Consulta de renda CNIS</strong>
            <br />
            <br />
            Permite consultar a sua renda que foi encontrada no Cadastro Nacional de Informações Sociais (CNIS), bases do Governo Federal. São informações de renda de trabalho e benefícios pagos pelo Instituto Nacional do Seguro Social (INSS).
            <br />
            <br />
            <br />
            <strong>Consulta da renda identificada no CNIS</strong>
            <br />
            <br />
            Permite consultar as rendas identificadas no CNIS nos últimos 12 meses, como informações de rendas de trabalho ou benefícios pagos pelo INSS. Essas informações são atualizadas mensalmente e se você não tiver nenhuma renda identificada. essa opção não será exibida. 
            <br />
            <br />
            <br />
            <strong>Renda atualizada automaticamente pelo Governo Federal</strong>
            <br />
            <br />
            Aqui você vê as rendas que o Governo Federal atualizou no seu Cadastro Único de forma automática. Essas informações não foram incluídas pelo responsável familiar nem pelo município. Elas mostram rendas do seu trabalho ou benefícios do INSS recebidos nos últimos 12 meses. Se você não teve nenhuma renda atualizada automaticamente, essa opção não será exibida no aplicativo. 
            <br />
            <br />
            <br />
          </div>
        </AjudaRapida>
      }
    </>
  );

  return (
    <MainContainer
      userNav={isLogado()}
      navTitle={obterNomeDoPerfil(perfil, situacaoCadastroFamilia)}
      titleBarraAuxiliar={"Consulta de renda CNIS"}
      barraAuxiliar={true}
      stoppedTitle={true}
      navFixed
      navGoBack={sair}
      navSeparator
      navCaixaMensagens={isLogado() ? caixaMensagens : undefined}
      navLogout={isLogado() ? logout : undefined}>
      <div className={styles.container}>
        {telaPrincipal}
      </div>
    </MainContainer>
  );
};

export default ConsultaRenda;
